import { TRPCClientError } from '@trpc/client';
import {
  errorCode,
  errorMessage,
  isAuthError,
  needsMfa,
  needsStepUp,
  requestReference,
} from './errors';

/**
 * Every error the console shows, in one place.
 *
 * ── ONE SYSTEM, AND WHY IT IS GLOBAL RATHER THAN A CONVENTION ─────────────────────────────────
 *
 * Errors used to be drawn wherever the failing control happened to be — a red banner under a
 * form, a line inside a panel, a sentence in a dialog — in six hand-typed variants across some
 * sixty places. The banners that forgot `whitespace-pre-line` ran a multi-part refusal together
 * into one sentence, and the period-lock dialog was one of them. A convention cannot fix that,
 * because the next banner is written by somebody who has not read it.
 *
 * So the query client's own handlers report every failed query and every failed mutation here
 * (`lib/trpc.ts`), and `ErrorToaster` is the only thing that draws them. A mutation nobody wrote
 * an error state for still produces a toast. What a call site CAN do is name the failure —
 * `meta: { errorTitle: 'Could not withdraw that invoice' }` — so the notice says what failed and
 * the server's sentence says why.
 *
 * ── TITLE, THEN DETAIL ────────────────────────────────────────────────────────────────────────
 *
 * The old banner showed the server's sentence on its own, and could, because it sat under the
 * form it was about. A toast floats free of the control, and "That is $20 more than 12 Kowhai St
 * has outstanding on invoices" is a riddle without the line above it saying it was the receipt
 * that failed. So every notice carries both: the title is the CALLER's (what was being done), the
 * detail is the SERVER's (why it was refused), and the detail keeps its line breaks — 23 routers
 * join several refusals with a newline, and every one of them now arrives as a list.
 *
 * ── STICKY, BECAUSE THESE ARE MONEY REFUSALS ──────────────────────────────────────────────────
 *
 * An error stays until somebody dismisses it. "July 2026 is locked" vanishing after five seconds
 * while the operator was reading the form is an error they never saw, followed by them pressing
 * the button again. The stack is capped instead, and the same failure repeated updates one
 * notice rather than adding another.
 *
 * FRAMEWORK-FREE — a plain subscribable store — because the query client that reports into it is
 * built before React renders, outside any component. `ErrorToaster` reads it with
 * `useSyncExternalStore`.
 */

export interface ErrorNotice {
  /** Stable across repeats of the same failure — see `report`. */
  id: string;
  /** What was being done. The caller's words. */
  title: string;
  /** Why it failed. The server's words, newline-separated when it refused for several reasons. */
  detail: string | null;
  /** How many times this failure has happened since it was last dismissed. */
  count: number;
  /** The one thing a notice can offer besides being dismissed. */
  action: 'verify' | null;
  /**
   * The server's id for the request that failed, shortened — see `requestReference`. Null when
   * the failure never reached the server (no connection, or a check the form made itself), because
   * then there is no log line for it to find.
   */
  reference: string | null;
}

/**
 * How many notices can be on screen at once.
 *
 * FOUR, because they are sticky: a fifth distinct failure before anybody has dismissed the first
 * is somebody working through a bad batch, and the oldest of five is the one least likely still
 * to matter. It is dropped rather than hidden, because a notice nobody can reach is not a notice.
 */
export const MAX_NOTICES = 4;

/**
 * A failure as it is remembered after its notice has gone — what the notifications panel's
 * Errors view lists.
 *
 * ── WHY THERE IS A HISTORY AT ALL ─────────────────────────────────────────────────────────────
 *
 * A notice is dismissed, dropped as the fifth of a bad batch, or cleared by `resolveQuery` the
 * moment the thing it described starts working again. Each of those is right for the SCREEN, and
 * each leaves somebody who glanced away unable to say what the red card said — or to read out the
 * reference to whoever they are reporting it to. So every failure is also written here, and
 * nothing that happens to its notice takes it out.
 *
 * ── IN MEMORY, FOR THIS SIGN-IN ONLY ──────────────────────────────────────────────────────────
 *
 * Not `localStorage`. A detail can carry a property's address and an owner's name ("That is $20
 * more than 12 Kowhai St has outstanding"), and anything written to the browser outlives the tab,
 * the session and the person. It is cleared with the notices on sign-out and org switch, for the
 * same reason they are.
 */
export interface RecentError {
  /** The notice's id, so a repeat updates its entry rather than adding another. */
  id: string;
  title: string;
  detail: string | null;
  /**
   * How many times it has failed this session — NOT reset by dismissing its notice. The notice
   * counts since it was last cleared; the history counts since sign-in.
   */
  count: number;
  reference: string | null;
  /** When it last failed, as an ISO instant. */
  at: string;
}

/**
 * How many the history keeps.
 *
 * TWENTY: enough to cover a bad batch worked through end to end, few enough that the panel's list
 * stays something you read rather than scroll. The oldest is dropped — this is a recent-errors
 * list, not a log, and the server's own log is what the reference finds.
 */
export const MAX_RECENT_ERRORS = 20;

type Listener = () => void;

let notices: readonly ErrorNotice[] = [];
let history: readonly RecentError[] = [];
let sequence = 0;
const listeners = new Set<Listener>();

function publish(
  next: readonly ErrorNotice[],
  nextHistory: readonly RecentError[] = history,
): void {
  notices = next;
  history = nextHistory;
  for (const listener of listeners) listener();
}

/** Record a failure in the history — newest first, one entry per id. */
function remember(notice: ErrorNotice, counted: boolean): readonly RecentError[] {
  const existing = history.find((entry) => entry.id === notice.id);
  const entry: RecentError = {
    id: notice.id,
    title: notice.title,
    detail: notice.detail,
    reference: notice.reference,
    // A first entry is one failure however it arrived — a quiet repeat whose entry was cleared
    // from the panel is still a failure that happened.
    count: existing === undefined ? 1 : existing.count + (counted ? 1 : 0),
    at: new Date().toISOString(),
  };
  if (existing && !counted) {
    // A quiet repeat: newest wording, same place. Moving it would reorder the list under somebody
    // reading it every time a poll missed.
    return history.map((other) => (other.id === notice.id ? { ...entry, at: existing.at } : other));
  }
  return [entry, ...history.filter((other) => other.id !== notice.id)].slice(0, MAX_RECENT_ERRORS);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Newest first. The same array until something changes, as `useSyncExternalStore` requires. */
export function getNotices(): readonly ErrorNotice[] {
  return notices;
}

/** Newest first, and the same array until something changes — like `getNotices`. */
export function getRecentErrors(): readonly RecentError[] {
  return history;
}

/** Empty the history. The panel's Clear — the notices on screen are left alone. */
export function clearRecentErrors(): void {
  if (history.length > 0) publish(notices, []);
}

export interface ReportInput {
  /**
   * What makes two failures "the same one". Omitted, every report is distinct.
   *
   * A repeat does not stack a second notice: it replaces the first with the newest wording, moves
   * it to the top and counts it. That count is not decoration — pressing Approve three times and
   * being told once looks exactly like the button doing nothing twice.
   */
  id?: string;
  title: string;
  detail?: string | null;
  action?: 'verify' | null;
  reference?: string | null;
  /**
   * A repeat that nobody CAUSED — a background refetch failing again, a poll that keeps missing.
   * The notice's wording is refreshed in place, but it is not counted, not moved and not
   * announced again. Counting is for a person pressing a button twice; a poll failing every
   * thirty seconds would otherwise interrupt a screen reader twice a minute for as long as the
   * API is down.
   */
  quietRepeat?: boolean;
}

export function report(input: ReportInput): string {
  sequence += 1;
  const id = input.id ?? `notice-${String(sequence)}`;
  const existing = notices.find((notice) => notice.id === id);

  if (existing && input.quietRepeat === true) {
    const refreshed: ErrorNotice = {
      ...existing,
      title: input.title,
      detail: input.detail ?? null,
      // The newest failure's, so the reference read out finds the latest attempt, not the first.
      reference: input.reference ?? null,
    };
    if (
      refreshed.title !== existing.title ||
      refreshed.detail !== existing.detail ||
      refreshed.reference !== existing.reference
    ) {
      publish(
        notices.map((notice) => (notice.id === id ? refreshed : notice)),
        remember(refreshed, false),
      );
    }
    return id;
  }

  const notice: ErrorNotice = {
    id,
    title: input.title,
    detail: input.detail ?? null,
    count: (existing?.count ?? 0) + 1,
    action: input.action ?? null,
    reference: input.reference ?? null,
  };

  publish(
    [notice, ...notices.filter((other) => other.id !== id)].slice(0, MAX_NOTICES),
    remember(notice, true),
  );
  return id;
}

export function dismiss(id: string): void {
  if (!notices.some((notice) => notice.id === id)) return;
  publish(notices.filter((notice) => notice.id !== id));
}

/**
 * For tests, and for signing out — one person's errors are not the next person's. The history
 * goes with the notices: it is the same facts, kept longer.
 */
export function dismissAll(): void {
  if (notices.length > 0 || history.length > 0) publish([], []);
}

// ── Reading an error into words ──────────────────────────────────────────────

/** The id every can't-reach-the-server failure shares, so twelve failed queries are one notice. */
export const CONNECTION_NOTICE = 'connection';

/** The id every step-up refusal shares. Cleared by the MFA page once the code is accepted. */
export const STEP_UP_NOTICE = 'step-up';

/**
 * No answer from the API at all — as opposed to an answer that was a refusal.
 *
 * A tRPC error with no `data` never reached a procedure: the fetch failed (offline, DNS, the API
 * restarting), or something in between answered with a page that is not JSON — a proxy's 502,
 * which tRPC surfaces as a parse error reading "Unexpected token '<'". Neither message is
 * anything a property manager can act on, and both say the same thing: the server was not there.
 */
export function isConnectionFailure(error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    return error.data === undefined || error.data === null;
  }
  // A thrown TypeError is what `fetch` raises for a network failure outside tRPC — an upload
  // straight to object storage, say.
  return error instanceof TypeError && /fetch|network/i.test(error.message);
}

/**
 * The server's own sentence, when it has one worth showing. Null when it does not.
 *
 * `errorMessage` with the fallback taken away: the fallback used to stand in for a missing
 * reason, and in a notice the title already does that, so repeating it as the detail would say
 * the same thing twice.
 */
export function describeError(error: unknown): string | null {
  if (isConnectionFailure(error)) return CONNECTION_DETAIL;
  const said = errorMessage(error, '');
  return said === '' ? null : said;
}

/** For something the page was READING. Asking again cannot change anything, so it may say so. */
export const CONNECTION_DETAIL =
  'ProManage could not be reached. Check your connection, then try again.';

/**
 * For something somebody asked to be DONE, when no answer came back.
 *
 * NEVER "TRY AGAIN", and this is the one sentence in the module that is about money rather than
 * wording. A request that got no answer may still have been carried out — the connection can drop
 * after the server commits and before the reply arrives — and on an append-only ledger a receipt,
 * a bill payment or a batch posted twice is corrected only by a reversing journal. So the notice
 * refuses to claim an outcome it does not know, and points at where the answer is instead.
 */
export const UNKNOWN_OUTCOME_DETAIL =
  'ProManage did not answer, so it is not known whether that went through. ' +
  'Check that it did not before doing it again.';

/**
 * Report a failure in words a caller chose. The one function a component calls itself — for a
 * `try`/`catch` around something the query client never saw, like an upload to object storage.
 */
export interface NotifyOptions {
  id?: string;
  /**
   * Whether this was a CHANGE rather than a read — which decides what may be said when no answer
   * came back. See `UNKNOWN_OUTCOME_DETAIL`. Defaults to a read.
   */
  changing?: boolean;
  /**
   * What only the caller knows about this failure, in place of the server's sentence when it
   * returns one. The transaction queue uses it: it knows which refusals are provably decided
   * before anything posts, and says "check the ledger" for every other kind.
   */
  detail?: ((error: unknown) => string | null) | undefined;
  /** See `ReportInput.quietRepeat`. Queries set it; a person pressing a button again does not. */
  quietRepeat?: boolean;
  /**
   * This was a check of what somebody TYPED — a password, an authenticator code — so its
   * UNAUTHORIZED means "that was wrong", not "your session expired", and it is reported like any
   * other refusal. Without it, a wrong password would be swallowed as an expiry and the sign-in
   * page would never say why it did not work.
   */
  credentials?: boolean;
}

export function notifyError(error: unknown, title: string, options: NotifyOptions = {}): void {
  // Redirected, not reported — see below. Unless it was a check of what was typed.
  if (isAuthError(error) && options.credentials !== true) return;
  if (needsMfa(error)) return;

  if (needsStepUp(error)) {
    reportStepUp();
    return;
  }

  const own = options.detail?.(error) ?? null;
  const id = options.id === undefined ? {} : { id: options.id };
  const quiet = options.quietRepeat === true ? { quietRepeat: true } : {};

  if (isConnectionFailure(error)) {
    if (options.changing === true) {
      // NOT folded into the shared connection notice. A read failing next to it would replace
      // this wording with "try again" — the one thing this notice must never say.
      report({ ...id, title, detail: own ?? UNKNOWN_OUTCOME_DETAIL });
      return;
    }
    report({
      id: CONNECTION_NOTICE,
      title: 'Could not reach ProManage',
      detail: CONNECTION_DETAIL,
      ...quiet,
    });
    return;
  }

  report({
    ...id,
    title,
    detail: own ?? describeError(error),
    reference: requestReference(error),
    ...quiet,
  });
}

/**
 * Report something the FORM caught before anything was sent — the domain's own list of what is
 * wrong with a draft, from `tenancyInvoiceIssues` and its siblings.
 *
 * ONE NOTICE FOR THE LIST, keyed by the form, so pressing Save again with the same mistakes
 * updates it rather than stacking a second copy.
 */
export function notifyProblems(title: string, problems: readonly string[], id: string): void {
  if (problems.length === 0) {
    dismiss(id);
    return;
  }
  report({ id, title, detail: problems.join('\n') });
}

function reportStepUp(): void {
  report({
    id: STEP_UP_NOTICE,
    title: 'Verify it’s you',
    // `errorMessage`'s own sentence for this case, so the two can never say different things.
    detail: 'Confirm your authenticator code before moving money.',
    action: 'verify',
  });
}

// ── What the query client reports ────────────────────────────────────────────

/**
 * The shape the query client's handlers pass. Declared here rather than imported from TanStack so
 * this module can be tested without a query client, and so `meta` reads as what it is.
 */
export interface ReportedFailure {
  /** A stable string for the thing that failed — the query hash, or the mutation's key. */
  key: string | null;
  meta:
    | {
        errorTitle?: string;
        errorToast?: false;
        errorDetail?: (error: unknown) => string | null;
        credentials?: true;
      }
    | undefined;
  /** Queries only: whether there was already data on screen, which makes this a stale page. */
  hadData?: boolean;
}

/**
 * A failed QUERY — something the page was trying to show.
 *
 * NOT FOUND IS NOT REPORTED. A mistyped ref is answered by the page itself — "No such invoice"
 * with the right way to find one — and a toast repeating that on top would be a second copy of a
 * sentence the operator is already reading. A missing record is an answer, not a failure.
 *
 * A REFETCH THAT FAILS UNDER DATA ALREADY ON SCREEN says so, because that is the dangerous case:
 * the figures still look fine and are no longer current. A trust balance that stopped refreshing
 * an hour ago is not something to find out about by accident.
 */
export function reportQueryFailure(error: unknown, failure: ReportedFailure): void {
  if (failure.meta?.errorToast === false) return;
  if (errorCode(error) === 'NOT_FOUND') return;

  const title =
    failure.hadData === true
      ? 'Could not refresh — what is shown may be out of date'
      : (failure.meta?.errorTitle ?? 'Part of this page could not be loaded');

  notifyError(error, title, {
    ...(failure.key === null ? {} : { id: `query:${failure.key}` }),
    detail: failure.meta?.errorDetail,
    // Nobody pressed anything: a refetch on focus, a poll, a retry. Say it once.
    quietRepeat: true,
  });
}

/**
 * A failed MUTATION — something somebody asked to be done.
 *
 * Keyed by the mutation, so the same button failing twice is one notice counted twice. tRPC gives
 * every mutation a key from its procedure path; a hand-built `useMutation` without one gets a
 * notice of its own each time.
 */
export function reportMutationFailure(error: unknown, failure: ReportedFailure): void {
  if (failure.meta?.errorToast === false) return;

  notifyError(error, failure.meta?.errorTitle ?? 'That could not be saved', {
    ...(failure.key === null ? {} : { id: `mutation:${failure.key}` }),
    changing: true,
    detail: failure.meta?.errorDetail,
    credentials: failure.meta?.credentials === true,
  });
}

// ── And when it stops being true ─────────────────────────────────────────────

/**
 * A query that loaded. Its own notice, if it had one, is no longer true — and neither is "could
 * not reach ProManage", because something just did.
 *
 * WHAT THE OLD BANNERS DID FOR FREE, and what a sticky notice has to be taught. A banner under a
 * form vanished the moment the form was fixed and saved; a toast that outlived the fix would sit
 * there claiming a failure the operator has already dealt with, and the next real one would be
 * read past as more of the same.
 */
export function resolveQuery(key: string): void {
  dismiss(`query:${key}`);
  dismiss(CONNECTION_NOTICE);
}

/**
 * A mutation that succeeded. Its notice goes — including a lost-answer warning, because the same
 * action has now visibly gone through, which is the answer the warning was waiting for.
 *
 * KEYED BY PROCEDURE, the same key a failure is counted by: approving a bill that failed and then
 * approving it successfully clears the failure. The cost is that a success on ANOTHER record through
 * the same procedure clears it too — acceptable because almost every mutation in the console is
 * bound to the one record whose page it is on, which is exactly how the banner it replaces behaved.
 */
export function resolveMutation(key: string): void {
  dismiss(`mutation:${key}`);
}

/*
 * WHAT IS DELIBERATELY NEVER REPORTED:
 *
 *   · An EXPIRED SESSION. `handleAuthExpiry` sends the user to the login page with a way back,
 *     and a toast saying "not signed in" over the login form would be describing the screen they
 *     are already looking at.
 *   · MFA NOT YET SATISFIED. The router guard sends them to the code prompt for the same reason.
 *
 * A STEP-UP refusal IS reported — once, keyed — with a Verify action, because it happens to a
 * signed-in, fully permitted person in the middle of their work, and "confirm your code" is the
 * one error here whose fix is a button.
 */
